import {
  editorConnectionSchema,
  launchSchema,
  workshopHistorySchema,
  workshopSessionSchema,
  workshopSolutionViewSchema,
} from '@mio/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { apiRequest } from '@/shared/api/client';
import { homeSummaryQuery } from '@/shared/api/queries/home';
import { programKeys } from '@/shared/api/queries/program';

import {
  beginEditorSessionRecovery,
  reclaimEditorSession,
  rememberEditorSession,
} from './workspace-session';

import type { CurrentWorkspace } from '@mio/shared';

export function useWorkshopSession(activityId: string | null) {
  return useQuery({
    queryKey: ['workshop', 'session', activityId],
    enabled: Boolean(activityId),
    queryFn: ({ signal }) =>
      apiRequest(`/workshop/activities/${activityId}`, workshopSessionSchema, { signal }),
  });
}

export function useIdeLaunch(workspace: CurrentWorkspace['workspace']) {
  return useMutation({
    mutationFn: async (options?: { recoverDisconnected: boolean }) => {
      if (!workspace?.activeAttemptId || workspace.state !== 'ready')
        throw new Error('Wait for the workspace to be ready.');
      const recovery = options?.recoverDisconnected
        ? await beginEditorSessionRecovery(workspace)
        : null;
      // A retry or Refresh IDE after a rejected launch first adopts the accepted
      // identity if the tab that owned it has closed, so the server renews the
      // same owner instead of waiting to prove the previous browser absent.
      const sessionId = recovery?.sessionId ?? (await reclaimEditorSession(workspace));
      const launch = await apiRequest(
        `/workshop/attempts/${workspace.activeAttemptId}/launch`,
        launchSchema,
        {
          method: 'POST',
          body: {
            sessionId,
            expectedGeneration: workspace.generation,
            expectedAttemptId: workspace.activeAttemptId,
            ...(options?.recoverDisconnected ? { recoverDisconnected: true } : {}),
          },
        },
      );
      recovery?.commit();
      // Only a server-accepted session can become the next tab's resume identity.
      rememberEditorSession(workspace, sessionId);
      return {
        context: `${workspace.id}:${workspace.generation}:${workspace.activeAttemptId}`,
        // Reconnecting requests a fresh status probe instead of reusing cached
        // extension health. This isolates probes; it does not acknowledge iframe loading.
        // Never put grants in keys.
        launchId: crypto.randomUUID(),
        launch,
      };
    },
  });
}

export function useEditorConnection(
  workspace: CurrentWorkspace['workspace'],
  launchedContext: string | null,
  launchId?: string,
) {
  const context = workspace?.activeAttemptId
    ? `${workspace.id}:${workspace.generation}:${workspace.activeAttemptId}`
    : null;
  return useQuery({
    queryKey: ['workshop', 'editor-connection', context, launchId ?? null],
    enabled: workspace?.state === 'ready' && context !== null && launchedContext === context,
    queryFn: ({ signal }) =>
      apiRequest(
        `/workshop/attempts/${workspace?.activeAttemptId}/editor-connection?readiness=1`,
        editorConnectionSchema,
        { signal },
      ),
    retry: false,
    // A connected editor needs only a slow heartbeat. While it is not connected,
    // probe quickly at first (the iframe usually attaches within seconds), then
    // back off so an unattended tab does not hammer the API; React Query already
    // pauses the interval while the tab is hidden.
    refetchInterval: (query) =>
      query.state.status === 'error' ||
      (query.state.data?.connected && query.state.data.workspaceReady !== false)
        ? 5000
        : query.state.dataUpdateCount < 20
          ? 1000
          : query.state.dataUpdateCount < 50
            ? 2000
            : 5000,
  });
}

export function useWorkshopEvidence(attemptId: string | null) {
  const client = useQueryClient();
  const history = useInfiniteQuery({
    queryKey: ['workshop', 'history', attemptId, 'pages'],
    refetchOnWindowFocus: true,
    enabled: Boolean(attemptId),
    initialPageParam: null as string | null,
    queryFn: ({ signal, pageParam }) =>
      apiRequest(
        `/workshop/attempts/${attemptId}/history${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''}`,
        workshopHistorySchema,
        { signal },
      ),
    getNextPageParam: (page) => page.nextCursor,
    select: (data) => ({
      ...data,
      submissions: data.pages.flatMap((page) => page.submissions),
      policy: data.pages[0]?.policy ?? null,
    }),
    refetchInterval: (query) => {
      const pending = query.state.data?.pages[0]?.submissions.find((submission) =>
        ['queued', 'evaluating'].includes(submission.status),
      );
      if (!pending) return false;
      // A normal review lands within a couple of minutes; a stuck queue should not be
      // polled every three seconds for an hour, even while the page is hidden.
      const elapsed = Date.now() - new Date(pending.createdAt).getTime();
      return elapsed < 120_000 ? 3000 : elapsed < 600_000 ? 5000 : 10_000;
    },
  });
  const completed =
    history.data?.submissions.some((submission) => submission.status === 'passed') ?? false;
  useEffect(() => {
    if (!completed) return;
    void client.invalidateQueries({ queryKey: programKeys.full() });
    void client.invalidateQueries({ queryKey: homeSummaryQuery.queryKey });
    void client.invalidateQueries({ queryKey: ['workshop', 'session'] });
  }, [client, completed]);
  return history;
}

/** The authored reference for a revealed attempt; immutable once published. */
export function useWorkshopSolution(attemptId: string | null, revealed: boolean) {
  return useQuery({
    queryKey: ['workshop', 'solution', attemptId],
    enabled: Boolean(attemptId) && revealed,
    queryFn: ({ signal }) =>
      apiRequest(`/workshop/attempts/${attemptId}/solution`, workshopSolutionViewSchema, {
        signal,
      }),
    staleTime: Infinity,
    retry: false,
  });
}
